import React, { useEffect, useState, useContext } from "react";
import "./AddVenue.css";
import { useLocation, useNavigate } from "react-router-dom";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import CurrentStep from "./CurrentStep";
import Loading from "../../components/Loader";
import { ToastContainer } from "react-toastify";
import Success from "./Success";
import { useTranslation } from 'react-i18next';
import AuthContext from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const StepTwo = ({ update, status, setStatus }) => {
    const { t } = useTranslation("addvenue");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);

  // Initial state for working hours
  const initialWorkingHours = {
    Saturday: { enabled: false, from: "9:00", to: "17:00" },
    Sunday: { enabled: false, from: "9:00", to: "17:00" },
    Monday: { enabled: false, from: "9:00", to: "17:00" },
    Tuesday: { enabled: false, from: "9:00", to: "17:00" },
    Wednesday: { enabled: false, from: "9:00", to: "17:00" },
    Thursday: { enabled: false, from: "9:00", to: "17:00" },
    Friday: { enabled: false, from: "9:00", to: "17:00" },
  };
   const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const currentLang = getLangFromLocalStorage();

  const convertToLocal = (utcTime) => {
    const [hours, minutes] = utcTime.split(":").map(Number);
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, 0, 0); // Set UTC time

    // Get local time
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();

    return `${localHours.toString().padStart(2, "0")}:${localMinutes
      .toString()
      .padStart(2, "0")}`;
  };

  const mapWorkingDaysToWorkingHours = (workingDays) => {
    // Initialize the workingHours object with default values for all days
    const workingHours = {
      Saturday: { enabled: false, from: "9:00", to: "17:00" },
      Sunday: { enabled: false, from: "9:00", to: "17:00" },
      Monday: { enabled: false, from: "9:00", to: "17:00" },
      Tuesday: { enabled: false, from: "9:00", to: "17:00" },
      Wednesday: { enabled: false, from: "9:00", to: "17:00" },
      Thursday: { enabled: false, from: "9:00", to: "17:00" },
      Friday: { enabled: false, from: "9:00", to: "17:00" },
    };

    // Helper function to convert day number to day name
    const getDayName = (dayNumber) => {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      return days[dayNumber];
    };

    // Update workingHours with data from workingDays
    workingDays.forEach((day) => {
      const dayName = getDayName(day.day); // Convert day number to day name
      workingHours[dayName] = {
        enabled: true, // Mark the day as enabled
        from: convertToLocal(day.start_time), // Set the 'from' time
        to: convertToLocal(day.end_time), // Set the 'to' time
      };
    });

    return workingHours;
  };

  // Load state from localStorage or use initial state
  const [workingHours, setWorkingHours] = useState(() => {
    const savedState = localStorage.getItem("workingHours"); // Retrieve saved state
    if (savedState) {
      try {
        return JSON.parse(savedState); // Parse the saved state
      } catch (error) {
        console.error("Error parsing saved state:", error);
        return initialWorkingHours; // Fallback to initial state
      }
    }
    return initialWorkingHours; // Use initial state if no saved state exists
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("workingHours", JSON.stringify(workingHours)); // Save state as JSON string
  }, [workingHours]);

  // State to enable/disable all days at once
  const [enableAll, setEnableAll] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const [editVenue, setEditVenue] = useState(
    searchParams.get("edit") === "true"
  );

  // Automatically update the "Enable All" checkbox based on the state of individual days
  useEffect(() => {
    const allEnabled = Object.values(workingHours).every((day) => day.enabled);
    setEnableAll(allEnabled);
  }, [workingHours]);

  useEffect(() => {
    setEditVenue(searchParams.get("edit") === "true");
  }, [searchParams]);

  // Handle enabling/disabling all days
  const handleEnableAll = () => {
    const newEnableAll = !enableAll;
    setEnableAll(newEnableAll);

    const updatedWorkingHours = { ...workingHours };
    for (const day in updatedWorkingHours) {
      updatedWorkingHours[day].enabled = newEnableAll;
    }
    setWorkingHours(updatedWorkingHours);
  };

  // Handle enabling/disabling a specific day
  const handleDayToggle = (day) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      },
    }));
  };

  // Handle time change for a specific day and field (from/to)
  const handleTimeChange = (day, field, newTime) => {
    if (!newTime) {
      if (field === "from") {
        newTime = "9:00";
      } else {
        newTime = "17:00";
      }
    }
    setWorkingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: newTime,
      },
    }));
  };

  const hasErrors = () => {
    let hasErrors = false;

    // Loop through all days and validate enabled ones
    for (const day in workingHours) {
      if (workingHours[day].enabled) {
        const { from, to } = workingHours[day];
        const fromTime = new Date(`1970-01-01 ${from}`);
        const toTime = new Date(`1970-01-01 ${to}`);

        // Check if "From" time is later than or equal to "To" time
        if (fromTime >= toTime) {
          alert(
            t("stepTwo.invalidTimeRange")
          );
          hasErrors = true;
          break; // Stop checking further if an error is found
        }
      }
    }
    return hasErrors;
  };

  useEffect(() => {
    if (status === "Success") {
      // Set a timeout to reset the status after 3 seconds
      const timer = setTimeout(() => {
        setStatus(""); // Reset status to empty string
      }, 2000); // 3000 milliseconds = 3 seconds

      // Cleanup the timer if the component unmounts or status changes
      return () => clearTimeout(timer);
    }
  }, [status]);

  // Handle navigation to the next step
  const handleNavigateWithParams = (step) => {
    // If no errors, proceed to the next step
    if (!hasErrors()) {
      navigate({
        pathname: location.pathname,
        search: `?step=${step}`,
      });
    }
  };

  const [loading, setLoading] = useState(editVenue);
  const [error, setError] = useState(null);
  const id = localStorage.getItem("venueId"); // Get the 'query' param

  useEffect(() => {
    const fetchVenues = async () => {
      if (!isAuthenticated) {
        setError(new Error("No authentication token found. Please log in."));
        setLoading(false);
        navigate("/");
        return;
      }
      try {
        const venueResponse = await fetch(`${baseUrl}/api/v1/attnd-venue/venues/${id}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        });

        if (!venueResponse.ok) {
          if (venueResponse.status === 401) {
            navigate("/login");
            throw new Error("Unauthorized access. Please log in again.");
          } else if (venueResponse.status === 404) {
            alert("404 Not found.");
            navigate(-1);
            throw new Error("404 Not found.");
          } else {
            throw new Error(`An error occurred: ${venueResponse.statusText}`);
          }
        }

        const venue = await venueResponse.json();
        const venueData = venue.working_days;

        setWorkingHours(mapWorkingDaysToWorkingHours(venueData));
        setLoading(false);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        console.log(workingHours);
      }
    };

    if (editVenue) fetchVenues();
  }, [baseUrl]);

  if (loading) {
    return (
      <div
        style={{
          height: "calc(100vh - 400px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="venue-venues-container">
        Error fetching venue: {error.message}
      </div>
    );
  }

  return (
    <div className={"steps"} style={editVenue ? { margin: "0" } : {}}>
      <CurrentStep step={2} />
      <div className={"Dates"}>
        <h4>{t("stepTwo.workingHoursTitle")}</h4>
        <div className={"Dates-container"}>
          <div className={"Row"}>
            <div className={"Day"} style={{ width: "100%" }}>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={enableAll}
                  onChange={handleEnableAll}
                />
                <span className="slider"></span>
              </label>
              <p className={"week"}>{t("stepTwo.enableAllWeek")}</p>
            </div>
          </div>
          {Object.keys(workingHours).map((day) => (
            <div className={"Row"} key={day}>
              <div className={"Day"}>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={workingHours[day].enabled}
                    onChange={() => handleDayToggle(day)}
                  />
                  <span className="slider"></span>
                </label>
                <p className="day">{t(`days.${day}`)}</p>
              </div>
              <div className={"Date"}>
                <p>{t("stepTwo.fromLabel")}</p>
                <TimePicker
                  onChange={(newTime) => handleTimeChange(day, "from", newTime)}
                  value={workingHours[day].from}
                  format="hh:mm a"
                  disableClock={true}
                  clearIcon={null}
                  locale={currentLang === "ar" ? "ar" : undefined}
                  className={"time"}
                  disabled={!workingHours[day].enabled}
                />
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z"
                    fill="#888888"
                  />
                </svg>
              </div>
              <div className={"Date"} >
                <p>{t("stepTwo.toLabel")}</p>
                <TimePicker
                  onChange={(newTime) => handleTimeChange(day, "to", newTime)}
                  value={workingHours[day].to}
                  format="hh:mm a"
                  disableClock={true}
                  locale={currentLang === "ar" ? "ar" : undefined}
                  clearIcon={null}
                  className={"time"}
                  disabled={!workingHours[day].enabled}
                />
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 16.8C11.3 16.8 10.6 16.53 10.07 16L3.55002 9.48001C3.26002 9.19001 3.26002 8.71001 3.55002 8.42001C3.84002 8.13001 4.32002 8.13001 4.61002 8.42001L11.13 14.94C11.61 15.42 12.39 15.42 12.87 14.94L19.39 8.42001C19.68 8.13001 20.16 8.13001 20.45 8.42001C20.74 8.71001 20.74 9.19001 20.45 9.48001L13.93 16C13.4 16.53 12.7 16.8 12 16.8Z"
                    fill="#888888"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
        <div className={"buttons"}>
          <button className={"Cancel"} onClick={() => handleNavigateWithParams(1)}>
            {t("stepTwo.previous")}
          </button>
          <button
            style={status !== "loading" ? {} : { background: "#888888" }}
            className={"Next"}
            onClick={() => {
              if (hasErrors()) return;
              if (status === "loading") return;
              if (editVenue) update(workingHours, null, null, null, id);
              else handleNavigateWithParams(3);
            }}
          >
            {editVenue
              ? status === "loading"
                ? t("stepTwo.loading")
                : t("stepTwo.save")
              : t("stepTwo.next")}
          </button>
        </div>
      </div>
      {status === "Success" && (
        <div className={"message_Venue"}>
          <div>
            {status === "Success" && <Success />}
            {t("stepTwo.changesSaved")}
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default StepTwo;
